import { __decorate } from "tslib";
import { html, css, LitElement, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { customElement, property, query, queryAssignedElements, state, } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { Size as DialogSize } from './enums/size.js';
import { DialogEvent } from './enums/dialog-event.js';
import './viedoc-icon.js';
export { DialogSize, DialogEvent };
const supportsDialogElement = typeof HTMLDialogElement === 'function';
const animationDuration = css `250ms`;
let ViedocDialog = class ViedocDialog extends LitElement {
    constructor() {
        super(...arguments);
        this.open = false;
        this.preventCloseByClick = false;
        this.size = DialogSize.Medium;
        this.hideFooter = true;
        this.hideHeader = true;
    }
    addOpenListeners() {
        document.addEventListener('keydown', this.handleDocumentKeyDown);
    }
    removeOpenListeners() {
        document.removeEventListener('keydown', this.handleDocumentKeyDown);
    }
    handleDocumentKeyDown(event) {
        if (this.open && event.key === 'Escape') {
            event.stopPropagation();
            this.hide();
        }
    }
    connectedCallback() {
        super.connectedCallback();
        this.handleDocumentKeyDown = this.handleDocumentKeyDown.bind(this);
        this.addOpenListeners();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeOpenListeners();
    }
    updated(changedProperties) {
        if (changedProperties.has('open') && supportsDialogElement && this.dialog) {
            if (this.open) {
                this.dialog.showModal();
            }
            else {
                this.dialog.close();
            }
        }
    }
    // Dispatch an event after click cancel
    hide() {
        this.open = false;
        this.dispatchEvent(new Event(DialogEvent.Hide, {
            bubbles: true,
            composed: true,
        }));
    }
    // Call this public function to open dialog
    show() {
        this.open = true;
        this.dispatchEvent(new Event(DialogEvent.Show, {
            bubbles: true,
            composed: true,
        }));
    }
    handleHeaderSlotChange() {
        this.hideHeader = this.headerContent.length === 0;
    }
    handleFooterSlotChange() {
        this.hideFooter = this.footerContent.length === 0;
    }
    handleDialogClick(event) {
        if (event.target === this.dialog && !this.preventCloseByClick) {
            this.hide();
        }
    }
    renderDialogContent() {
        return html `<div
      class=${classMap({
            dialog__content: true,
            [`dialog__content--${this.size}`]: true,
            'dialog__content--no-header': this.hideHeader,
        })}
    >
      <button class="dialog__close-button" @click=${this.hide}>
        <viedoc-icon name="close"></viedoc-icon>
      </button>
      <header
        aria-hidden=${ifDefined(this.hideHeader ? true : undefined)}
        class=${classMap({
            'header--visible': !this.hideHeader,
        })}
      >
        <slot name="header" @slotchange=${this.handleHeaderSlotChange}></slot>
      </header>

      <main>
        <slot></slot>
      </main>
      <footer
        aria-hidden=${ifDefined(this.hideFooter ? true : undefined)}
        class=${classMap({
            'footer--visible': !this.hideFooter,
        })}
      >
        <slot name="footer" @slotchange=${this.handleFooterSlotChange}></slot>
      </footer>
    </div>`;
    }
    render() {
        /* eslint-disable lit-a11y/click-events-have-key-events */
        /* disabled becuase dialog can still be closed using esc key but listener
           is not on the same element as the click event that closes on click outside */
        if (supportsDialogElement) {
            return html `<dialog @click="${this.handleDialogClick}">
        ${this.renderDialogContent()}
      </dialog>`;
        }
        // Template for browsers that doesn't support dialog element.
        // Should be removed when support has gotten better.
        return html ` <div
      class=${classMap({
            'fallback-dialog-wrapper': true,
            'fallback-dialog-wrapper--visible': this.open,
        })}
    >
      <div
        class="fallback-dialog-overlay"
        @click="${this.preventCloseByClick ? nothing : this.hide}"
      ></div>
      <div class="fallback-dialog">${this.renderDialogContent()}</div>
    </div>`;
    }
};
ViedocDialog.styles = [
    css `
      :host {
        --viedoc-dialog-z-index: 1000;
        --dialog-size-small: 31.25rem; /* 500px */
        --dialog-size-medium: 43.75rem; /* 700px */
        --dialog-size-large: 62.5rem; /* 1000px */
        --dialog-main-spacing: 1.25rem; /* Padding main div */
      }

      dialog {
        padding: 0;
        border: none;
        background-color: transparent;
      }

      dialog[open] {
        display: flex;
      }

      dialog::backdrop {
        background-color: rgba(0, 0, 0, 0.6);
        animation: fadeIn ${animationDuration} ease-in;
      }

      dialog[open] .dialog__content {
        animation: fadeIn ${animationDuration} ease-in;
      }

      .dialog__content {
        --close-button-size: 2.25rem;
        --header-spacing-horizontal: 1.25rem;

        display: flex;
        flex-direction: column;
        font-family: var(--font-family-monument);
        background-color: var(--secondary-background-color);
        border-radius: 1.875rem;
        max-width: 100%;
      }

      .dialog__content--small {
        width: var(--dialog-size-small);
      }

      .dialog__content--medium {
        width: var(--dialog-size-medium);
      }

      .dialog__content--large {
        width: var(--dialog-size-large);
      }

      .dialog__content--no-header main {
        padding-top: 3.75rem; /* 60px */
      }

      .dialog__close-button {
        position: absolute;
        right: var(--header-spacing-horizontal);
        top: 1.375rem;
        height: var(--close-button-size);
        width: var(--close-button-size);
        background-color: var(--neutral-color);
        padding: 0.438rem;
        border-radius: 1.25rem;
        border: 0px;
        color: var(--color-gray-100);
        cursor: pointer;
      }

      .dialog__close-button:hover {
        background-color: var(--neutral-highlight-color);
      }

      main {
        min-height: 0;
        height: 100%;
        overflow-y: auto;
        overflow-x: hidden;
        padding: var(--dialog-main-spacing);
      }

      header {
        flex-shrink: 0;
        display: none;
        background-color: var(--secondary-background-color);
        color: var(--primary-text-color);
        padding-top: 1.375rem;
        padding-right: calc(
          var(--header-spacing-horizontal) + var(--close-button-size) + 0.5rem
        );
        padding-bottom: 1.375rem;
        padding-left: var(--header-spacing-horizontal);
        font-family: var(--font-family-viedoc);
        font-size: var(--font-size-lg);
        border-bottom: 0.125rem solid var(--primary-background-color);
        border-radius: 1.875rem 1.875rem 0rem 0rem;
      }

      footer {
        flex-shrink: 0;
        display: none;
        background-color: var(--secondary-background-color);
        color: var(--primary-text-color);
        padding: 1.375rem 1.25rem;
        border-top: 0.125rem solid var(--primary-background-color);
        border-radius: 0rem 0rem 1.875rem 1.875rem;
      }

      .footer--visible,
      .header--visible {
        display: block;
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
        }

        to {
          opacity: 1;
        }
      }

      /* Fallback for browsers that doesn't support dialog element, like Safari < 15.4 */
      .fallback-dialog-wrapper {
        opacity: 0;
        transition: opacity ${animationDuration} ease-in;
        z-index: var(--viedoc-dialog-z-index);
        visibility: hidden;
      }

      .fallback-dialog-wrapper--visible {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        opacity: 1;
        visibility: visible;
      }

      .fallback-dialog-overlay {
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        width: 100%;
        height: 100%;
        background-color: var(--color-gray-100);
        opacity: 0.6;
      }

      .fallback-dialog {
        position: fixed;
        display: flex;
        max-height: calc(100% - 2rem);
        max-width: calc(100% - 2rem);
      }
    `,
];
__decorate([
    property({ type: Boolean, reflect: true })
], ViedocDialog.prototype, "open", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ViedocDialog.prototype, "preventCloseByClick", void 0);
__decorate([
    property({ type: String })
], ViedocDialog.prototype, "size", void 0);
__decorate([
    state()
], ViedocDialog.prototype, "hideFooter", void 0);
__decorate([
    state()
], ViedocDialog.prototype, "hideHeader", void 0);
__decorate([
    query('dialog')
], ViedocDialog.prototype, "dialog", void 0);
__decorate([
    queryAssignedElements({ slot: 'header' })
], ViedocDialog.prototype, "headerContent", void 0);
__decorate([
    queryAssignedElements({ slot: 'footer' })
], ViedocDialog.prototype, "footerContent", void 0);
ViedocDialog = __decorate([
    customElement('viedoc-dialog')
], ViedocDialog);
export { ViedocDialog };
